import React from 'react';
import ReactMarkdown from 'react-markdown';

import { countBulletLines } from '@revelio/iso-utility';

import styles from './MarkdownDescription.module.css';

const hasMarkdownContent = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') {
      return child.trim().length > 0;
    }
    if (typeof child === 'number') {
      return true;
    }
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      const nested = child.props.children;
      return nested ? hasMarkdownContent(nested) : false;
    }
    return false;
  });

const BOLD_ONLY_LINE = /^\s*\*\*([^*]+)\*\*\s*$/;

const promoteSectionHeaders = (markdown: string): string =>
  markdown
    .split('\n')
    .map((line) => {
      const text = line.match(BOLD_ONLY_LINE)?.[1]?.trim() ?? '';
      const isAllCaps =
        text.length > 0 && text === text.toUpperCase() && /[A-Z]/.test(text);
      return isAllCaps ? `### ${text}` : line;
    })
    .join('\n');

/**
 * Whether a description should render as markdown rather than plain text.
 * Already-formatted descriptions always do; otherwise we only switch to
 * markdown when there are real bullet lines to render.
 */
export const shouldRenderMarkdown = (
  description: string,
  isFormatted: boolean
): boolean => {
  if (isFormatted) {
    return true;
  }
  return countBulletLines(description) > 0;
};

export const MarkdownDescription = ({
  description,
}: {
  description: string;
}): React.ReactElement => (
  <div className={styles.markdownContent}>
    <ReactMarkdown
      components={{
        p: ({ children }) => {
          if (!hasMarkdownContent(children)) {
            return null;
          }
          return (
            <p
              className={`${styles.sectionDetails} ${styles.markdownParagraph}`}
            >
              {children}
            </p>
          );
        },
        h3: ({ children }) => (
          <h3
            className={`${styles.sectionDetails} ${styles.markdownParagraph} ${styles.markdownSectionHeader}`}
          >
            {children}
          </h3>
        ),
        ul: ({ children }) => (
          <ul className={styles.markdownList}>{children}</ul>
        ),
        li: ({ children }) => (
          <li className={styles.markdownListItem}>{children}</li>
        ),
      }}
    >
      {promoteSectionHeaders(description.trimEnd())}
    </ReactMarkdown>
  </div>
);
